import { FaqItem } from "@webcore/components/domain/content-site/sections/FaqSection";

// Answer-engine-optimized FAQ copy. Each answer is self-contained (40-60 words) so it can be
// used on the website, in sales collateral, or as AI-citation source copy.
const whatIsDatafabriq: FaqItem = {
  question: "What is DataFabrIQ?",
  answer:
    "DataFabrIQ is an operational data platform that unifies your finance and GTM systems — Salesforce, HubSpot, Stripe, and NetSuite — into one clean, trusted, auditable single source of truth. It's the data foundation your dashboards, workflows, and AI all read from: the operational software you'd build if you had a data team, already built.",
};

const whatIsSingleSourceOfTruth: FaqItem = {
  question: "What is a single source of truth for finance and GTM data?",
  answer:
    "A single source of truth is one governed, always-current record of your numbers that every team and tool reads from, instead of reconciling separate exports. DataFabrIQ creates it by unifying Salesforce, HubSpot, Stripe, and NetSuite into one auditable dataset, so finance, sales, and engineering all trust the same figures.",
};

const howDoesItTraceANumber: FaqItem = {
  question: "How does DataFabrIQ trace a number back to its source?",
  answer:
    "DataFabrIQ provides full data lineage on every line. It shows how each number was built — the derivation logic and source SQL behind it — so you can trace any figure back to its source record in two clicks, not a three-day reconciliation. Every transformation carries a plain-English description plus the underlying SQL.",
};

const whichSystemsDoesItConnectTo: FaqItem = {
  question: "Which systems does DataFabrIQ connect to?",
  answer:
    "DataFabrIQ connects to your finance and GTM stack, including Salesforce, HubSpot, Stripe, NetSuite, QuickBooks, Maxio, Authorize.net, Shopify, and major ad platforms (Meta, Google, Amazon, TikTok). It also supports custom sources such as spreadsheets and budgets, unifying them all into one auditable source of truth.",
};

const howDifferentFromBiTool: FaqItem = {
  question: "How is DataFabrIQ different from a BI tool or dashboard?",
  answer:
    "BI tools visualize whatever data they're pointed at and ask you to take the numbers on faith. DataFabrIQ is the trusted data foundation underneath — every number traces to its source record — and it ships ready-to-run workflows like MRR closes, commission runs, and variance reports, not just charts.",
};

const howDifferentFromSemanticLayer: FaqItem = {
  question: "How is DataFabrIQ different from a semantic layer like dbt or Cube?",
  answer:
    "dbt and Cube are metric-definition layers that assume a data team writing YAML and SQL, plus a separate BI tool on top. DataFabrIQ bundles data unification, transformation, lineage, governance, and the operational workflows themselves — built by AI, auditable to the row, with no data team required.",
};

const doINeedADataTeam: FaqItem = {
  question: "Do I need a data team to use DataFabrIQ?",
  answer:
    "No. DataFabrIQ is the operational software you'd build if you had a data team — already built. Its AI performs the data-engineering work (transformation, reconciliation, business logic) that would normally require engineers writing SQL, so teams without a data function get a governed, auditable source of truth.",
};

const howDoesItImproveAiRoi: FaqItem = {
  question: "How does DataFabrIQ improve ROI on AI investment?",
  answer:
    "AI built on messy, siloed data breaks the moment it leaves the chat window. DataFabrIQ gives AI a governed data foundation, so solutions hold up in production and deliver ROI across the whole company — not just in the demo — turning prototypes into reliable, company-wide systems.",
};

const canItScaleAiPastPrototype: FaqItem = {
  question: "Can DataFabrIQ help me scale AI past the prototype?",
  answer:
    "Yes. Anyone can vibe-code a prototype; making it something the whole organization relies on is a different challenge. DataFabrIQ supplies the governed data foundation, enterprise-grade hosting, security, and auditability that let AI solutions hold up long past the demo and scale across the business.",
};

const doesItMakeFinanceMoreEfficient: FaqItem = {
  question: "Does DataFabrIQ make my finance team more efficient?",
  answer:
    "Yes. DataFabrIQ ships purpose-built, auditable workflows for MRR/ARR closes, commission calculations, variance reports, and AR aging. Board-level answers are ready on demand instead of after a multi-week reconciliation, and complex commission rules move out of error-prone spreadsheets into traceable calculations.",
};

export const homepageFaqs: FaqItem[] = [
  whatIsDatafabriq,
  whatIsSingleSourceOfTruth,
  howDoesItTraceANumber,
  howDifferentFromBiTool,
  doINeedADataTeam,
];

export const saasFaqs: FaqItem[] = [
  howDifferentFromSemanticLayer,
  whichSystemsDoesItConnectTo,
  howDoesItImproveAiRoi,
  canItScaleAiPastPrototype,
  doesItMakeFinanceMoreEfficient,
];

// Ecommerce landing page FAQ — objection-handling copy from the ecommerce LP (Wrenchless v2.5).
const doINeedToKnowSql: FaqItem = {
  question: "Do I need to know SQL?",
  answer:
    "No. Every calculation is there to read whenever you want to check it — writing them is our job. Our CTO's rule: the goal is to hide the SQL from you entirely.",
};

const doINeedATechnicalTeam: FaqItem = {
  question: "Do I need a technical team?",
  answer:
    "No. Companies use DataFabrIQ instead of hiring a $250K+ data or AI engineer. Connecting your systems takes one call; we do all of the building.",
};

const isUnfinishedBusinessLogicABlocker: FaqItem = {
  question: "We haven't nailed down all of our own business logic yet. Is that a blocker?",
  answer:
    "No. Tell us how you think about your business and we encode it. Refining those rules is part of the two weeks.",
};

const canYouKeepBuildingAfterSetup: FaqItem = {
  question: "Can you keep building things for us after the initial setup?",
  answer:
    "Yes. DataFabrIQ has a forward-deployed engineering team that acts as your right hand: new views, new workflows, new automations as the business grows. Our clients treat this as their data team.",
};

const whatIfConnectorIsMissing: FaqItem = {
  question: "What if you don't have a connector for one of my tools?",
  answer:
    "Your data comes in as a direct file import immediately, and new connectors typically take about a week to build.",
};

const whatAccessDoYouNeed: FaqItem = {
  question: "What access do you need to connect my systems?",
  answer:
    "An API key or a login you grant us, the same as connecting any other tool to your stack. Nothing more, and revocable at any time.",
};

const isMyDataSecure: FaqItem = {
  question: "Is my data secure?",
  answer: "[ Pending — security overview to be confirmed before this page ships to production. ]",
};

export const ecommerceFaqs: FaqItem[] = [
  doINeedToKnowSql,
  doINeedATechnicalTeam,
  isUnfinishedBusinessLogicABlocker,
  canYouKeepBuildingAfterSetup,
  whatIfConnectorIsMissing,
  whatAccessDoYouNeed,
  isMyDataSecure,
];
